'use client'

import { createPushModal } from 'pushmodal'
import type React from 'react'
import { AppModal } from '../apps-v2/app-modal'
import ProjectStatisticsModal from '../dashboard/project-statistics/project-statistics-modal'
import AddProductModal from '../project/toolbox-overlay/shopping-admin/add-product-modal'
import ShoppingProductFormModal from '../project/toolbox-overlay/shopping-admin/shopping-form-modal'
import { AIGeneratorV2Modal } from './ai-generator-v2'
import { AiTeamPhotoModal } from './ai-team-photo'
import { AlertModal } from './alert-modal'
import { AnalyticsModal } from './analytics'
import { CancelConfirmationModal } from './cancel-confirmation'
import { ChangelogModal } from './changelog-modal'
import CodingLogDetailModal from './coding-log-detail-modal'
import ConfirmationModal from './confirmation-modal'
import ContactUsModal from './contact-us'
import DatabaseFormDialogWrapper from './database/Form/DatabaseFormDialogWrapper'
import { DeleteTableColumnModal } from './database/Form/DeleteTableColumn'
import { UpsertTableColumnModal } from './database/Form/UpsertTableColumn'
import { DatabaseModalV2 } from './database/index'
import DeleteAccountConfirmModal from './delete-account-confirm'
import DiscordModal from './discord-modal'
import DownloadUsersModal from './download-users-modal'
import EditStarterKitModal from './edit-starter-kit'
import EditTemplateModal from './edit-template'
import { ElementLibraryModal } from './element-library-modal'
import EmbedConfirmationModal from './embed-confirmation-modal'
import { FormEditModal } from './form-edit-modal'
import GiftCoinModal from './gift-coin'
import ImportCsvModal from './import-csv-modal'
import ImportGmailModal from './import-gmail-modal'
import IntroVideoModal from './intro-video'
// Static imports for all modals
import LoginModal from './login'
import { MediaLibraryModal } from './media-library'
import MigrateProjectModal from './migrate-project-modal'
import OnboardingSurveyModal from './onboarding-survey'
import PricingModal from './pricing'
import ProductFormModal from './product-form-modal'
import ProjectToTemplateModal from './project-to-template'
import { PublishModal } from './publish-modal'
import { PublishResultModal } from './publish-result'
import { ReferralModal } from './referral'
import RenameProjectModal from './rename-project-modal'
import RenameWorkspaceModal from './rename-workspace'
import SendEmailModal from './send-email-modal'
import { SetDesignStyleModal } from './set-design-style'
import SettingModal from './setting'
import ShareSendCoinsModal from './share-send-coins'
import StripeConnectModal from './stripe-connect'
import { SubscribeCancelModal } from './subscribe-cancel'
import { SubscribeSuccessfulModal } from './subscribe-successful'
import { SuperPromptModal } from './super-prompt-modal'
import { SuperPromptsModal } from './super-prompts-modal'
import { TableUploadModal } from './table-upload-modal'
import TransferOwnershipModal from './transfer-ownership'
import { UploadAssetsModal } from './upload-assets'
import { WorkspaceInsufficientCoinsModal } from './workspace-insufficient-coins-modal'
import WorkspaceSettingsModal from './workspace-settings'

const modals = {
  LoginModal,
  PricingModal,
  SettingModal,
  DatabaseModal: DatabaseModalV2,
  DatabaseFormModal: DatabaseFormDialogWrapper,
  AnalyticsModal,
  UploadAssetsModal,
  SetDesignStyleModal,
  UpsertTableColumnModal,
  DeleteTableColumnModal,
  IntroVideoModal,
  ContactUsModal,
  SendEmailModal,
  ImportCsvModal,
  ImportGmailModal,
  GiftCoinModal,
  ProjectToTemplateModal,
  EditTemplateModal,
  EditStarterKitModal,
  ReferralModal,
  AIGeneratorV2Modal,
  MediaLibraryModal,
  ElementLibraryModal,
  SuperPromptsModal,
  SuperPromptModal,
  ProductFormModal,
  StripeConnectModal,
  ConfirmationModal,
  ShareSendCoinsModal,
  PublishResultModal,
  CancelConfirmationModal,
  SubscribeSuccessfulModal,
  SubscribeCancelModal,
  PublishModal,
  AlertModal,
  WorkspaceInsufficientCoinsModal,
  AiTeamPhotoModal,
  AppModal,
  ProjectStatisticsModal,
  TableUploadModal,
  FormEditModal,
  ChangelogModal,
  ShoppingProductFormModal,
  AddProductModal,
  OnboardingSurveyModal,
  EmbedConfirmationModal,
  DiscordModal,
  RenameWorkspaceModal,
  MigrateProjectModal,
  RenameProjectModal,
  DeleteAccountConfirmModal,
  DownloadUsersModal,
  CodingLogDetailModal,
  TransferOwnershipModal,
  WorkspaceSettingsModal,
} as const

export const {
  pushModal,
  popModal,
  popAllModals,
  replaceWithModal,
  useOnPushModal,
  onPushModal,
  ModalProvider,
} = createPushModal({
  modals,
})

// Export the modals type for external use
export type ModalProps = {
  [K in keyof typeof modals]: (typeof modals)[K] extends React.ComponentType<
    infer P
  >
    ? P extends Record<string, never>
      ? never
      : P
    : never
}
